import React, { useEffect, useState } from 'react';
import './Login.css';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResendingConfirmation, setIsResendingConfirmation] = useState(false);
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);
  const [showConfirmationNotice, setShowConfirmationNotice] = useState(false);
  const { toast } = useToast();

  const { signIn, resendConfirmationEmail, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const shouldShowConfirmationByQuery = searchParams.get('confirmEmail') === '1';
  const prefilledEmailFromQuery = (searchParams.get('email') || '').trim();
  const shouldShowConfirmationByState = Boolean(location.state?.pendingEmailConfirmation);
  const prefilledEmailFromState = String(location.state?.email || '').trim();

  useEffect(() => {
    if (!loading && user) {
      if (user.role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (user.role === 'merchant') {
        navigate('/logista', { replace: true });
      } else if (user.role === 'courier') {
        navigate('/entregas', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const pendingConfirmation = shouldShowConfirmationByQuery || shouldShowConfirmationByState;
    const suggestedEmail = prefilledEmailFromQuery || prefilledEmailFromState;

    if (suggestedEmail && !email) {
      setEmail(suggestedEmail);
    }

    if (pendingConfirmation) {
      setShowConfirmationNotice(true);
      setShowResendConfirmation(true);
    }
  }, [
    shouldShowConfirmationByQuery,
    shouldShowConfirmationByState,
    prefilledEmailFromQuery,
    prefilledEmailFromState,
    email,
  ]);

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      toast({
        title: "Sucesso!",
        description: "Login realizado com sucesso.",
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }
      const statusCode = Number(err?.statusCode || err?.status || 0);
      const backendDetail = String(
        err?.originalError?.detail ||
        err?.message ||
        ''
      ).toLowerCase();
      let description = err?.getUserFriendlyMessage?.() || err?.message || "Erro ao fazer login.";

      if (statusCode === 404 || backendDetail.includes('conta inexistente')) {
        description = "Conta Inexistente";
      } else if (statusCode === 401 && backendDetail.includes('email nao confirmado')) {
        description = "Email nao confirmado. Verifique sua caixa de entrada.";
        setShowResendConfirmation(true);
        setShowConfirmationNotice(true);
      } else if (statusCode === 401) {
        description = "Senha ou email incorrentos";
      }

      toast({
        variant: "destructive",
        title: "Erro no Login",
        description,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResendConfirmation() {
    if (!email || isResendingConfirmation) return;
    setIsResendingConfirmation(true);
    try {
      await resendConfirmationEmail(email);
      toast({
        title: "Confirmacao reenviada",
        description: "Se o email estiver cadastrado, enviaremos as instrucoes de confirmacao.",
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }
      const statusCode = Number(err?.statusCode || err?.status || 0);
      toast({
        variant: "destructive",
        title: "Erro ao reenviar confirmacao",
        description:
          statusCode === 504
            ? "Timeout ao reenviar email. Verifique a configuracao SMTP no Supabase."
            : (err?.getUserFriendlyMessage?.() || err?.message || "Nao foi possivel reenviar o email."),
      });
    } finally {
      setIsResendingConfirmation(false);
    }
  }

  return (
    <div className="login-container">
      <div className="login-box auth-card shadow-2xl border border-gray-100">
        <h1 className="login-title">CorreFood</h1>
        <p className="login-subtitle">Faça login em sua conta</p>

        <form onSubmit={handleSubmit} className="auth-form space-y-4">
          <input
            className="auth-input w-full p-3 rounded-lg border focus:ring-2 focus:ring-orange-500 outline-none"
            type="email"
            placeholder="E-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <input
            className="auth-input w-full p-3 rounded-lg border focus:ring-2 focus:ring-orange-500 outline-none"
            type="password"
            placeholder="Senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <button
            type="submit"
            disabled={isSubmitting || loading}
            className="auth-submit-btn w-full text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting || loading ? 'Carregando...' : 'Entrar'}
          </button>
        </form>

        {showConfirmationNotice && (
          <div className="confirmation-callout">
            <p className="confirmation-callout-text">
              Por segurança, confirme seu cadastro através do e-mail enviado para você.
            </p>
            {showResendConfirmation && (
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={isResendingConfirmation || !email}
                className="auth-secondary-btn"
              >
                {isResendingConfirmation ? 'Reenviando confirmação...' : 'Reenviar e-mail de confirmação'}
              </button>
            )}
          </div>
        )}

        <div className="login-footer">
          <p className="auth-footer-text text-center text-sm text-gray-600 mt-4">
            <a href="/forgot-password" className="text-orange-500 hover:underline font-semibold">
              Esqueci minha senha
            </a>
          </p>
          <p className="auth-footer-text text-center text-sm text-gray-600 mt-4">
            Nao tem uma conta?{' '}
            <a href="/register" className="text-orange-500 hover:underline font-semibold">
              Cadastre-se
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
